import {
  Button,
  Card,
  CardHeader,
  Checkbox,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
} from "@mui/material";
import { WcGrid } from "@bpipartners/webui";

import {
  useGetEnabledTenantFormPartsByFormIdQuery,
  useGetDisabledTenantFormPartsByFormIdQuery,
  GQLDisabledTenantFormPartsByFormIdRow,
  GQLEnabledTenantFormPartsByFormIdRow,
} from "@quales/data-access/graphql";
import { useTranslations } from "next-intl";
import { useTheme } from "@quales/utils/theme";
import React from "react";

interface ConfigTenantFormPartsProps {
  onSubmitClicked: (data: GQLEnabledTenantFormPartsByFormIdRow[]) => void;
  formID: string;
}

const not = (a: readonly any[], b: readonly any[]) =>
  a.filter((value: any) => b.indexOf(value) === -1);

const intersection = (a: readonly any[], b: readonly any[]) =>
  a.filter((value: any) => b.indexOf(value) !== -1);

const union = (a: readonly any[], b: readonly any[]) => [...a, ...not(b, a)];

export const ConfigTenantFormParts = React.forwardRef(
  (props: ConfigTenantFormPartsProps, ref) => {
    const { onSubmitClicked, formID } = props;
    const [checked, setChecked] = React.useState<
      readonly GQLEnabledTenantFormPartsByFormIdRow[]
    >([]);
    const t = useTranslations();
    const [configuredPartsList, setconfiguredPartsList] =
      React.useState<GQLEnabledTenantFormPartsByFormIdRow[]>();
    const [disabledPartsList, setdisabledPartsList] =
      React.useState<GQLDisabledTenantFormPartsByFormIdRow[]>();
    const { loading: enabledLoading, data: enabledData } =
      useGetEnabledTenantFormPartsByFormIdQuery({
        variables: { formID: formID },
        fetchPolicy: "network-only",
      });
    const { loading: disabledLoading, data: disabledData } =
      useGetDisabledTenantFormPartsByFormIdQuery({
        variables: { formID: formID },
        fetchPolicy: "network-only",
      });

    const leftChecked = intersection(checked, configuredPartsList);
    const rightChecked = intersection(checked, disabledPartsList);
    const theme = useTheme();
    const handleToggle = (value: any) => () => {
      const currentIndex = checked.indexOf(value);
      const newChecked = [...checked];

      if (currentIndex === -1) {
        newChecked.push(value);
      } else {
        newChecked.splice(currentIndex, 1);
      }

      setChecked(newChecked);
    };

    const numberOfChecked = (items: readonly any[]) =>
      intersection(checked, items).length;

    const handleToggleAll = (items: readonly any[]) => () => {
      if (numberOfChecked(items) === items.length) {
        setChecked(not(checked, items));
      } else {
        setChecked(union(checked, items));
      }
    };

    const handleCheckedRight = () => {
      setdisabledPartsList(disabledPartsList.concat(leftChecked));
      setconfiguredPartsList(not(configuredPartsList, leftChecked));
      setChecked(not(checked, leftChecked));
    };

    const handleCheckedLeft = () => {
      setconfiguredPartsList(configuredPartsList.concat(rightChecked));
      setdisabledPartsList(not(disabledPartsList, rightChecked));
      setChecked(not(checked, rightChecked));
    };

    React.useEffect(() => {
      if (
        !enabledLoading &&
        enabledData &&
        enabledData.EnabledTenantFormPartsByFormID &&
        enabledData.EnabledTenantFormPartsByFormID.length > 0
      ) {
        setconfiguredPartsList(enabledData.EnabledTenantFormPartsByFormID);
      } else {
        setconfiguredPartsList([]);
      }
    }, [enabledLoading, enabledData]);

    React.useEffect(() => {
      if (
        !disabledLoading &&
        disabledData &&
        disabledData.DisabledTenantFormPartsByFormID &&
        disabledData.DisabledTenantFormPartsByFormID.length > 0
      ) {
        setdisabledPartsList(disabledData.DisabledTenantFormPartsByFormID);
      } else {
        setdisabledPartsList([]);
      }
    }, [disabledLoading, disabledData]);

    React.useImperativeHandle(ref, () => ({
      onSubmit() {
        if (onSubmitClicked) {
          onSubmitClicked(configuredPartsList);
        }
      },
    }));

    const customList = (title: React.ReactNode, items: readonly any[]) => (
      <Stack>
        <Card
          sx={{
            backgroundColor: theme.appColors.surfaceContainerLow,
          }}
          variant="outlined"
        >
          <CardHeader
            sx={{ px: 2, py: 1 }}
            avatar={
              <Checkbox
                onClick={handleToggleAll(items)}
                checked={
                  numberOfChecked(items) === items.length && items.length !== 0
                }
                indeterminate={
                  numberOfChecked(items) !== items.length &&
                  numberOfChecked(items) !== 0
                }
                disabled={items.length === 0}
                inputProps={{
                  "aria-label": "all items selected",
                }}
              />
            }
            title={title}
            subheader={`${numberOfChecked(items)}/${items.length} selected`}
          />
          <Divider />
          <List
            sx={{
              width: 400,
              height: theme.dimensions.baseHeight
                ? theme.dimensions.baseHeight - 250
                : "auto",
              backgroundColor: theme.appColors.surfaceContainerLow,
              overflow: "auto",
            }}
            dense
            component="div"
            role="list"
          >
            {items.map((value: any) => {
              const labelId = `transfer-list-all-item-${value.name}-label`;
              return (
                <ListItem
                  key={value.formID}
                  role="listitem"
                  onClick={handleToggle(value)}
                >
                  <ListItemIcon>
                    <Checkbox
                      checked={checked.includes(value)}
                      tabIndex={-1}
                      disableRipple
                      inputProps={{
                        "aria-labelledby": labelId,
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText id={labelId} primary={`${value.name}`} />
                </ListItem>
              );
            })}
            <ListItem />
          </List>
        </Card>
      </Stack>
    );

    return (
      <Stack width={900} id="RolePermissionsComposite">
        {configuredPartsList && (
          <WcGrid
            container
            spacing={2}
            justifyContent="center"
            alignItems="center"
          >
            <WcGrid>
              {customList(t("labels.disabledFormParts"), disabledPartsList)}
            </WcGrid>
            <WcGrid>
              <WcGrid container direction="column" alignItems="center">
                <Button
                  sx={{ my: 0.5 }}
                  variant="contained"
                  size="small"
                  onClick={handleCheckedLeft}
                  disabled={rightChecked.length === 0}
                  aria-label="move selected right"
                >
                  &gt;
                </Button>
                <Button
                  sx={{ my: 0.5 }}
                  variant="contained"
                  size="small"
                  onClick={handleCheckedRight}
                  disabled={leftChecked.length === 0}
                  aria-label="move selected left"
                >
                  &lt;
                </Button>
              </WcGrid>
            </WcGrid>
            <WcGrid>
              {customList(t("labels.enabledFormParts"), configuredPartsList)}
            </WcGrid>
          </WcGrid>
        )}
      </Stack>
    );
  }
);
ConfigTenantFormParts.displayName = "ConfigTenantFormParts";
export default ConfigTenantFormParts;
